import React, { FC } from 'react';
import styled from 'styled-components';
import { Input as ReactstrapInput, InputProps } from 'reactstrap';
import { TranslateFn } from '@blocs.i18n';
import { FieldWrapper, FieldWrapperProps } from './FieldWrapper';

const StyledReactstrapInput = styled(ReactstrapInput)<{ $error: boolean }>`
  background: ${({ theme }) => theme.designSystem.bgColors.card};
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.default)};
  outline: none;

  &&:focus,
  &&:focus-visible {
    box-shadow: none;
    border: 1px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.error : theme.designSystem.borderColors.select};
    outline: 4px solid
      ${({ theme, $error }) =>
        $error ? theme.designSystem.borderColors.inputFocusError : theme.designSystem.borderColors.inputFocus};
  }

  &&:disabled {
    box-shadow: none;
    background: ${({ theme }) => theme.designSystem.bgColors.inputDisabled};
    border: 1px solid ${({ theme }) => theme.designSystem.borderColors.default};
  }
`;

/** Types */
type ReactstrapInputProps = Pick<
  InputProps,
  'bsSize' | 'valid' | 'invalid' | 'innerRef' | 'plaintext' | 'addon' | 'type' | 'multiple'
>;
type HtmlInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur' | 'value' | 'multiple' | 'id'
>;

export type BaseInputProps = ReactstrapInputProps &
  HtmlInputProps & {
    /**
     * callback invoked with the value of the text field
     */
    onChange?: (newValue: string) => void;
    /** callback invoked when the input loses focus */
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    /**
     * value of this input
     */
    value: string;
    /** test id value */
    'data-testid'?: string;
    /** qa id value */
    'data-qa-id'?: string;
    /** name attribute for the input */
    name: string;
    /** number of rows to show in for the height of the textarea, only passed into the input if type="textarea" */
    rows?: number;
  };

/**
 * A simple, controlled input
 * @name BaseInput
 * @description this is to be used as a base for other input components, do not import this directly into application code
 * it does not include a label, help text, validation or other form field features
 *
 * @example
 * const [value, setSelectedValue] = useState<string>('');
 * <BaseInput value={value} onChange={setSelectedValue} />
 */
export const BaseInput: FC<BaseInputProps> = ({
  onBlur,
  onChange,
  'data-testid': testId,
  'data-qa-id': qaId,
  type = 'text',
  value,
  name,
  rows,
  ...inputProps
}) => {
  const normalizedType = ['text', 'password', 'number', 'search', 'email', 'tel', 'textarea', 'url'].includes(type)
    ? type
    : 'text';

  return (
    <StyledReactstrapInput
      {...inputProps}
      onBlur={onBlur}
      id={name}
      name={name}
      data-testid={testId}
      data-qa-id={qaId}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      type={normalizedType}
      value={value === undefined ? '' : value}
      size={undefined}
      bsSize={undefined}
      $error={inputProps?.invalid}
      rows={type === 'textarea' && typeof rows === 'number' ? rows : undefined}
    />
  );
};

export interface WrappedBaseInputProps {
  wrapperProps?: FieldWrapperProps;
  inputProps?: BaseInputProps;
}

/**
 * A simple, controlled input with the standard form field wrapper
 * @name WrappedBaseInput
 * @description this is to be used as a base for other input components, do not import this directly into application code
 * it should be setup with a formik <Field /> around it which maps value, onChange, name, validation, etc into the props
 *
 * @example
 * const [value, setSelectedValue] = useState<string>('');
 * <WrappedBaseInput
 *    inputProps={{ value, onChange: setSelectedValue, type: 'text', name: 'input' }}
 *    wrapperProps={{ label: 'My Input', helpText: 'Please Fill This Out', id: 'input' }}
    />
 */
export const WrappedBaseInput: FC<WrappedBaseInputProps> = ({ wrapperProps = {}, inputProps }) => {
  return (
    <FieldWrapper {...wrapperProps}>
      <BaseInput {...inputProps} />
    </FieldWrapper>
  );
};

export const getHelpTextValue = ({
  maxLength,
  helpTextProp,
  currentValue,
  t,
}: {
  maxLength?: number;
  helpTextProp?: string;
  currentValue?: string;
  t: TranslateFn;
}) => {
  let maxLenHelpText = '';
  if (typeof maxLength === 'number' && currentValue?.length >= maxLength) {
    return t('common:form.info.maxLengthReached', { maxLength });
  }

  if (typeof maxLength === 'number' && currentValue?.length < maxLength) {
    maxLenHelpText = t('common:form.info.maxLength', { maxLength });
  }

  return helpTextProp || maxLenHelpText;
};

// formik can provide the error for a field as a string, or array of strings, possibly an object of key/value pairs for nested fields as well
// this helper attempts to consolidate the various formats into a single stringified output
export const normalizeFormikError = (error: string | string[] | undefined) => {
  if (!error) {
    // falsy values get returned back as-is
    return error as undefined;
  }

  // if it's anything other than an array, just return it
  // this handles the case where formik returns a string or object with key / values
  if (!Array.isArray(error)) {
    return error?.toString();
  }

  // if its an array, filter out any falsy / empty values, then join the remaining ones together
  return error?.filter((e) => typeof e === 'string').join(', ');
};
